import React from 'react';
import { Edit as EditIcon } from '@mui/icons-material';
import { StatusPill, StatusVariant } from 'Components/StatusPill/StatusPill.styled';
import {
  PCard,
  ThumbWrap,
  Thumb,
  StatusSlot,
  CategoryChip,
  EditButton,
  DraftBadge,
  Body,
  Name,
  DescReveal,
  Desc,
  Cm,
  DueLabel,
  Creator,
  CreatorPhoto,
  CreatorInitial,
  CreatorName,
} from './CourseCardV2.styled';

export interface CourseCardV2StatusI {
  label: string;
  variant: StatusVariant;
}

export interface CourseCardV2CategoryI {
  name: string;
  color?: string;
}

export interface CourseCardV2CreatorI {
  fullName: string;
  photo: string | null;
}

export interface CourseCardV2PropsI {
  name: string;
  /** URL ya firmada lista para pintar. Nunca una key de S3: el card no firma nada. */
  thumbnailUrl: string | null;
  modulesText?: React.ReactNode;
  dueText?: React.ReactNode;
  // Los cuatro opcionales activan la variante extendida; sin ellos el card es el mínimo del Dashboard.
  /** Presente: el panel blanco se eleva en hover para revelarla. Ausente: el hover queda como el del Dashboard. */
  description?: string | null;
  status?: CourseCardV2StatusI | null;
  category?: CourseCardV2CategoryI | null;
  creator?: CourseCardV2CreatorI | null;
  // Caso admin: sin estas dos el card queda idéntico al de Dashboard/MyCourses.
  /** Botón de editar sobre el thumbnail. Su click no dispara el onClick del card. */
  onEdit?: (() => void) | null;
  /** Badge de borrador sobre el thumbnail. Llega ya traducido: el card no traduce. */
  draftLabel?: string | null;
  onClick: () => void;
}

export default function CourseCardV2({
  name,
  thumbnailUrl,
  modulesText,
  dueText,
  description,
  status,
  category,
  creator,
  onEdit,
  draftLabel,
  onClick,
}: CourseCardV2PropsI) {
  return (
    <PCard data-testid="course-card" onClick={onClick}>
      <ThumbWrap>
        <Thumb className="pcard-thumb" data-testid="course-card-thumb" imageUrl={thumbnailUrl} />
        {status && (
          <StatusSlot className="pcard-status" data-testid="course-card-status-slot">
            <StatusPill data-testid="course-card-status" variant={status.variant} small>
              {status.label}
            </StatusPill>
          </StatusSlot>
        )}
        {category && (
          <CategoryChip data-testid="course-card-category" chipColor={category.color}>
            {category.name}
          </CategoryChip>
        )}
        {draftLabel && <DraftBadge data-testid="course-card-draft">{draftLabel}</DraftBadge>}
        {onEdit && (
          <EditButton
            type="button"
            aria-label="editar"
            data-testid="course-card-edit"
            onClick={(event) => {
              event.stopPropagation();
              onEdit();
            }}
          >
            <EditIcon fontSize="small" />
          </EditButton>
        )}
      </ThumbWrap>
      <Body data-testid="course-card-body">
        <Name data-testid="course-card-name">{name}</Name>
        {description && (
          <DescReveal className="pcard-desc" data-testid="course-card-description">
            <Desc>{description}</Desc>
          </DescReveal>
        )}
        {modulesText && <Cm data-testid="course-card-modules">{modulesText}</Cm>}
        {dueText && <DueLabel data-testid="course-card-due">{dueText}</DueLabel>}
        {creator && (
          <Creator data-testid="course-card-creator">
            {creator.photo ? (
              <CreatorPhoto src={creator.photo} alt={creator.fullName} />
            ) : (
              <CreatorInitial data-testid="course-card-creator-initial">{creator.fullName.charAt(0)}</CreatorInitial>
            )}
            <CreatorName>{creator.fullName}</CreatorName>
          </Creator>
        )}
      </Body>
    </PCard>
  );
}
